import { resolveTimelineAxisLabel } from '../../../filters/lib/statisticsCompareHelpers';
import { PERIOD_YOY_GRANULARITY } from '../../../filters/lib/statisticsPeriodYoyHelpers';
import { getOrderStatisticsPrimaryPhase } from '../../pipeline/lib/orderPrimaryPhaseConfig';

const parseDateKey = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const resolveOrderAxisDate = (order, dateField = 'createdAt') =>
	dateField === 'completedAt' ? order?.completedAt : order?.createdAt;

const orderInDateRange = (order, startDate, endDate, dateField = 'createdAt') => {
	const axisDate = parseDateKey(resolveOrderAxisDate(order, dateField));
	if (!axisDate) return false;
	const start = parseDateKey(startDate);
	const end = parseDateKey(endDate);
	if (start && axisDate < start) return false;
	if (end) {
		const endDay = new Date(end);
		endDay.setHours(23, 59, 59, 999);
		if (axisDate > endDay) return false;
	}
	return true;
};

const orderMatchesYearPeriodSubset = (order, year, granularity, periodIndices, dateField = 'createdAt') => {
	const axisDate = parseDateKey(resolveOrderAxisDate(order, dateField));
	if (!axisDate || axisDate.getFullYear() !== year) return false;
	if (!Array.isArray(periodIndices) || !periodIndices.length) return true;

	if (granularity === PERIOD_YOY_GRANULARITY.QUARTER) {
		const quarter = Math.floor(axisDate.getMonth() / 3);
		return periodIndices.includes(quarter);
	}

	return periodIndices.includes(axisDate.getMonth());
};

/** 对比地图：基准/自定义区间列表 */
export const buildLineCompareMapPeriodsFromCompares = (compares = []) =>
	compares
		.filter((item) => (item.kind === 'anchor' || item.kind === 'custom') && item.periodStartDate && item.periodEndDate)
		.map((item) => ({
			id: item.key,
			label: resolveTimelineAxisLabel({
				kind: item.kind,
				title: item.title,
				periodLabel: item.periodLabel,
				startDate: item.periodStartDate,
				endDate: item.periodEndDate,
			}),
			startDate: item.periodStartDate,
			endDate: item.periodEndDate,
		}));

/** 对比地图：按年对比（各年一张图，沿用月/季子集） */
export const buildLineCompareMapPeriodsFromYoy = ({
	periodYoy = null,
	granularity = PERIOD_YOY_GRANULARITY.MONTH,
	periodIndices = null,
} = {}) => {
	const years = periodYoy?.years ?? [];
	return years.map((year) => ({
		id: `ym-${year}`,
		label: `${year}年`,
		year,
		granularity,
		periodIndices,
	}));
};

export const buildLineCompareMapPeriods = ({
	compares = [],
	yearMonthsMode = false,
	periodYoy = null,
	granularity = PERIOD_YOY_GRANULARITY.MONTH,
	periodIndices = null,
} = {}) => {
	if (yearMonthsMode) {
		return buildLineCompareMapPeriodsFromYoy({ periodYoy, granularity, periodIndices });
	}
	return buildLineCompareMapPeriodsFromCompares(compares);
};

export const filterOrdersForLineCompareMapPeriod = ({
	orders = [],
	period,
	selectedPhase,
	trendDateField = 'createdAt',
}) =>
	(orders ?? []).filter((order) => {
		if (selectedPhase && getOrderStatisticsPrimaryPhase(order) !== selectedPhase) return false;

		if (period?.year != null) {
			return orderMatchesYearPeriodSubset(order, period.year, period.granularity, period.periodIndices, trendDateField);
		}

		return orderInDateRange(order, period?.startDate, period?.endDate, trendDateField);
	});
